
/**
 * 移动端策略
*/
import { HasReachLimit } from '@/api';
import { i18n } from '@/i18n';
import { isOutLimit } from '../base';
import {
  WARNING_LOGO,
  WARNING_TITLE,
} from '../constants';
import { SceneConfigMapType, ComponentStrategyMapType, GetSceneParamsType, JumpButtonType, BusinessScene, MobileBusinessComponent, PlatformType, MobileBusinessComponentInfoMap } from '../type';

type MobileComponentStrategyMapType = ComponentStrategyMapType<MobileBusinessComponentInfoMap>;

/**
 * @description 链接异常时组件展示策略
 */
const errorLinkStrategy: MobileComponentStrategyMapType = {
  // logo
  [MobileBusinessComponent.LOGO]: '',

  // logo下标题
  [MobileBusinessComponent.LOGO_TITLE]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      return i18n('打开{{0}}微信去收款', true);
    }
    return i18n('{{0}}微信内完成收款', true);
  },

  // 主要文案
  [MobileBusinessComponent.MAIN_TEXT]: i18n('若链接无法打开，请让收款人用微信扫\n小程序码，完成收款操作'),

  // 小程序码 - 展示小鹅小程序码
  [MobileBusinessComponent.MINI_APP_QRCODE]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      return '//weremit-static.tenpay.com/upload-static/tgb/omxj1BEzrVW3ig6nm6Z871-image66.png';
    }
    return '//weremit-static.tenpay.com/upload-static/tgb/rlmhKfB9Mqi5e2zHXods1W-image67.png';
  },


  // 跳转按钮 - 不展示跳转按钮
  [MobileBusinessComponent.JUMP_BUTTON]: null,
};

/**
 * @description 通用营销场景组件展示策略
 */
const commonMarketingsStrategy: MobileComponentStrategyMapType = {
  // logo
  [MobileBusinessComponent.LOGO]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开且超限
    if (
      sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX
      && sceneParams.hasReachLimit !== HasReachLimit.IN_LIMIT
    ) {
      return WARNING_LOGO;
    }
    // 端内没有超限概念
    return '';
  },

  // logo下标题
  [MobileBusinessComponent.LOGO_TITLE]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开且超限
    if (
      sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX
      && sceneParams.hasReachLimit !== HasReachLimit.IN_LIMIT
    ) {
      return WARNING_TITLE;
    }

    return i18n('打开{{0}}微信', true);
  },

  // 主要文案
  [MobileBusinessComponent.MAIN_TEXT]: (sceneParams: GetSceneParamsType) => {
    // 如果超限
    if (isOutLimit(sceneParams)) {
      return i18n('若链接无法打开，请用微信扫一扫下方\n小程序码');
    }
    return i18n('点击下方按键即可\n在微信打开「微汇款」小程序');
  },

  // 小程序码
  [MobileBusinessComponent.MINI_APP_QRCODE]: (sceneParams: GetSceneParamsType) => {
    // 如果超限
    if (isOutLimit(sceneParams)) {
      return '//weremit-static.tenpay.com/upload-static/tgb/inU32Y0iGdCLpdHVkQH5qs-image68.png';
    }
    return '';
  },

  // 跳转按钮
  [MobileBusinessComponent.JUMP_BUTTON]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      // 端外未超限
      if (sceneParams.hasReachLimit === HasReachLimit.IN_LIMIT) {
        return {
          // 跳转按钮类型：开放标签 or urlScheme按钮
          buttonType: JumpButtonType.URL_SCHEME_BUTTON,
          // 开放标签使用的path
          urlScheme: sceneParams.urlScheme,
        };
      }
      // 端外超限
      return null;
    }

    // 端内
    return {
      // 跳转按钮类型：开放标签 or urlScheme按钮
      buttonType: JumpButtonType.OPEN,
      // 开放标签使用的path
      path: sceneParams.openPath,
    };
  },
};

/**
 * @description 小鹅快收 - 收款人视角场景组件展示策略
 */
const receiverStrategy: MobileComponentStrategyMapType = {
  // logo
  [MobileBusinessComponent.LOGO]: '',

  // logo下标题
  [MobileBusinessComponent.LOGO_TITLE]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      return i18n('打开{{0}}微信去收款', true);
    }
    return i18n('{{0}}微信内完成收款', true);
  },

  // 主要文案
  [MobileBusinessComponent.MAIN_TEXT]: (sceneParams: GetSceneParamsType) => {
    // 如果超限
    if (isOutLimit(sceneParams)) {
      return i18n('若链接无法打开，请用微信扫一扫下方\n小程序码，完成收款操作');
    }
    // 端外
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      return i18n('点击下方按键即可\n在微信打开「微汇款」小程序');
    }
    return i18n('将打开微信里的「微汇款」小程序完成收款');
  },

  // 小程序码
  [MobileBusinessComponent.MINI_APP_QRCODE]: (sceneParams: GetSceneParamsType) => {
    // 如果超限
    if (isOutLimit(sceneParams)) {
      return '//weremit-static.tenpay.com/upload-static/tgb/KvuObpph5LgJM5c7XndgOt-image69.png';
    }
    return '';
  },

  // 跳转按钮
  [MobileBusinessComponent.JUMP_BUTTON]: (sceneParams: GetSceneParamsType) => {
    console.log('sceneParams', sceneParams);
    // 如果是端外打开
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      return {
        buttonType: JumpButtonType.URL_SCHEME_BUTTON,
        urlScheme: sceneParams.urlScheme,
      };
    }

    // 端内没有超限概念
    return {
      // 跳转按钮类型：开放标签 or urlScheme按钮
      buttonType: JumpButtonType.OPEN,
      // 开放标签使用的path
      path: sceneParams.openPath,
    };
  },
};

/**
 * @description 小鹅汇款人视角场景组件展示策略
 */
const quickReceiptRemitterStrategy: MobileComponentStrategyMapType = {
  // logo
  [MobileBusinessComponent.LOGO]: '',

  // logo下标题
  [MobileBusinessComponent.LOGO_TITLE]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      return i18n('打开{{0}}微信去通知收款人', true);
    }
    return i18n('{{0}}微信内通知收款人', true);
  },

  // 主要文案
  [MobileBusinessComponent.MAIN_TEXT]: i18n('将打开微信里的「微汇款」小程序完成收款'),

  // 小程序码
  [MobileBusinessComponent.MINI_APP_QRCODE]: '',

  // 跳转按钮
  [MobileBusinessComponent.JUMP_BUTTON]: (sceneParams: GetSceneParamsType) => {
    // 如果是端外打开
    if (sceneParams.platform === PlatformType.MOBILE_OUTSIDE_WX) {
      return {
        // 跳转按钮类型：开放标签 or urlScheme按钮
        buttonType: JumpButtonType.URL_SCHEME_BUTTON,
        // 开放标签使用的path
        urlScheme: sceneParams.urlScheme,
      };
    }
    // 端内没有超限概念
    return {
      // 跳转按钮类型：开放标签 or urlScheme按钮
      buttonType: JumpButtonType.OPEN,
      // 开放标签使用的path
      path: sceneParams.openPath,
    };
  },

  // 次要文案
  [MobileBusinessComponent.SUB_TEXT]: i18n('或\n复制下方链接并发送给收款人：'),

  // 复制链接
  [MobileBusinessComponent.COPY_LINK]: (sceneParams: GetSceneParamsType) => sceneParams.copyLink,
};

/**
 * @description 组件展示策略Map
*/
export const sceneConfig: SceneConfigMapType<MobileBusinessComponentInfoMap> = {

  [BusinessScene.ERROR_LINK]: errorLinkStrategy,

  [BusinessScene.COMMON_MARKETINGS]: commonMarketingsStrategy,

  [BusinessScene.QUICK_RECEIPT_RECEIVER]: receiverStrategy,

  [BusinessScene.QUICK_RECEIPT_REMITTER]: quickReceiptRemitterStrategy,
};
